// Очередь отложенных правок каталога (фаза 3, этап B).
//
// Правки кабинета (категории сроков, родительский каталог, меню точки) уходили
// прямым POST/PATCH и без сети падали. Посмотреть меню офлайн стало можно снимком
// кэша (этап A) — а изменить нельзя было.
//
// Отдельно от `syncQueue` (кухня): у кухонных операций автор фиксируется в момент
// тапа, есть окно ≤5 минут и схлопывание взаимоотменяющих пар. Кабинету это не
// нужно — там один менеджер и обычный CRUD. Общее только одно: идемпотентность
// по `clientId`, который для create становится id сущности на сервере.

import { isSupportSession, SupportReadOnlyError } from '@/shared/auth';
import { t } from '@/shared/i18n';

import { offlineDb, type CabinetOp } from './db';

export type EnqueueCabinetInput = Omit<CabinetOp, 'createdAt' | 'attempts' | 'status'>;

const listeners = new Set<() => void>();

export function onCabinetEnqueued(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

/**
 * Поставить правку в очередь. Отправка ПРОБУЕТСЯ сразу (через слушателя), а не ждёт
 * события `online`: сеть на кухне и в офисе бывает и без смены `navigator.onLine`.
 */
export async function enqueueCabinetOp(
  input: EnqueueCabinetInput,
  now = Date.now(),
): Promise<void> {
  // Саппорт-сессия только на чтение (T-039) — см. `enqueue`.
  if (isSupportSession()) {
    throw new SupportReadOnlyError(t('support.readOnly'));
  }
  const op: CabinetOp = {
    ...input,
    createdAt: new Date(now).toISOString(),
    attempts: 0,
    status: 'pending',
  };
  await offlineDb.cabinetQueue.put(op);
  notify();
}

/** Операции к отправке в порядке постановки. `error` исключён — его двигает человек. */
export async function pendingCabinetOps(): Promise<CabinetOp[]> {
  const all = await offlineDb.cabinetQueue.orderBy('createdAt').toArray();
  return all.filter((op) => op.status === 'pending');
}

export async function cabinetQueueSize(): Promise<number> {
  return offlineDb.cabinetQueue.count();
}

export async function failedCabinetOps(): Promise<CabinetOp[]> {
  return offlineDb.cabinetQueue.where('status').equals('error').toArray();
}

/** Вернуть прерванные `syncing` в работу. Один раз при старте (как у syncQueue). */
export async function resumeInterruptedCabinet(): Promise<number> {
  const stuck = await offlineDb.cabinetQueue.where('status').equals('syncing').toArray();
  for (const op of stuck) {
    await offlineDb.cabinetQueue.update(op.clientId, { status: 'pending' });
  }
  return stuck.length;
}

/** Ручной повтор ошибок — кнопкой из индикатора синхронизации. */
export async function retryFailedCabinet(): Promise<number> {
  const failed = await failedCabinetOps();
  await offlineDb.cabinetQueue.bulkPut(
    failed.map((op) => ({ ...op, status: 'pending' as const, lastError: undefined })),
  );
  if (failed.length > 0) notify();
  return failed.length;
}

export async function clearCabinetQueue(): Promise<void> {
  await offlineDb.cabinetQueue.clear();
}
